import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

export default class extends Controller {
  static targets = ["input", "results", "hidden", "clearButton", "chevron"]
  static values = {
    url: String,
    fieldName: String
  }

  connect() {
    this.cachedHtml = null
    this.abortController = null
    this.lastLabelMousedownAt = 0
    this.hideResults()
    this.updateClearButtonVisibility()
    this.attachLabelListener()
    this.prefetch()
  }

  disconnect() {
    if (this.abortController) this.abortController.abort()
    this.detachLabelListener()
  }

  attachLabelListener() {
    this.labelEl = document.querySelector(`label[for="${this.inputTarget.id}"]`)
    if (!this.labelEl) return
    this.labelMousedownHandler = () => { this.lastLabelMousedownAt = Date.now() }
    this.labelEl.addEventListener("mousedown", this.labelMousedownHandler)
  }

  detachLabelListener() {
    if (this.labelEl && this.labelMousedownHandler) {
      this.labelEl.removeEventListener("mousedown", this.labelMousedownHandler)
    }
  }

  search() {
    const query = this.inputTarget.value.trim()
    this.performSearch(query)
    this.updateClearButtonVisibility()
  }

  setFocus() {
    this.inputTarget.focus()
  }

  handleFocus() {
    if (Date.now() - this.lastLabelMousedownAt < 300) return
    this.inputTarget.focus()
    if (this.cachedHtml) {
      this.renderHtml(this.cachedHtml)
      this.prefetch()
    } else {
      this.performSearch("", true)
    }
  }

  preventBlur(event) {
    event.preventDefault()
  }

  toggle() {
    if (this.resultsTarget.classList.contains("hidden")) {
      this.handleFocus()
    } else {
      this.inputTarget.focus()
      this.hideResults()
    }
  }

  clear() {
    this.inputTarget.value = ""
    this.hiddenTarget.value = ""
    this.hideResults()
    this.updateClearButtonVisibility()
    this.inputTarget.focus()
  }

  handleBlur() {
    this.hideResults()
  }

  updateClearButtonVisibility() {
    const hasValue = this.inputTarget.value.trim() !== ""
    if (this.hasClearButtonTarget) {
      this.clearButtonTarget.classList.toggle("hidden", !hasValue)
    }
    if (this.hasChevronTarget) {
      this.chevronTarget.classList.toggle("hidden", hasValue)
    }
  }

  prefetch() {
    this.fetchResults("").then(html => {
      if (html !== null) this.cachedHtml = html
    })
  }

  performSearch(query, showOnEmpty = false) {
    if (query === "" && !showOnEmpty) {
      this.hideResults()
      return
    }

    this.fetchResults(query).then(html => {
      if (html !== null) this.renderHtml(html)
    })
  }

  fetchResults(query) {
    if (this.abortController) this.abortController.abort()
    this.abortController = new AbortController()
    const signal = this.abortController.signal

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("query", query)

    return fetch(url, {
      headers: { "Accept": "text/vnd.turbo-stream.html" },
      signal
    })
    .then(response => response.text())
    .catch(err => {
      if (err.name === "AbortError") return null
      throw err
    })
  }

  renderHtml(html) {
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html
    const turboStream = tempDiv.querySelector("turbo-stream")
    const hasResults = turboStream && turboStream.innerHTML.includes("<li")

    Turbo.renderStreamMessage(html)

    if (hasResults) {
      this.showResults()
    } else {
      this.hideResults()
    }
  }

  hideResults() {
    if (this.abortController) this.abortController.abort()
    this.resultsTarget.classList.add("hidden")
    this.inputTarget.setAttribute("aria-expanded", "false")
  }

  showResults() {
    this.resultsTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  navigate(event) {
    const items = Array.from(this.resultsTarget.querySelectorAll('li'))
    const currentIndex = items.findIndex(item => item.classList.contains("bg-indigo-600"))

    switch(event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (currentIndex < items.length - 1) {
          this.highlightItem(currentIndex + 1)
        }
        break
      case "ArrowUp":
        event.preventDefault()
        if (currentIndex > 0) {
          this.highlightItem(currentIndex - 1)
        }
        break
      case "Enter":
        event.preventDefault()
        const selectedItem = items.find(item => item.classList.contains("bg-indigo-600"))
        if (selectedItem) {
          this.selectItem({ target: selectedItem, preventDefault: () => {} })
        }
        break
      case "Escape":
        event.preventDefault()
        this.hideResults()
        break
    }
  }

  highlightItem(event) {
    const items = Array.from(this.resultsTarget.querySelectorAll('li'))

    items.forEach(item => {
      item.classList.remove("bg-indigo-600", "text-white")
      item.classList.add("text-gray-900")
    })

    let itemToHighlight
    if (typeof event === 'number') {
      itemToHighlight = items[event]
    } else {
      itemToHighlight = event.target.closest('li')
    }

    if (itemToHighlight) {
      itemToHighlight.classList.remove("text-gray-900")
      itemToHighlight.classList.add("bg-indigo-600", "text-white")
      itemToHighlight.scrollIntoView({ block: "nearest" })
    }
  }

  selectItem(event) {
    event.preventDefault()
    const item = event.target.closest('li')
    if (!item) return

    const id = item.dataset.autocompleteIdValue
    const name = item.dataset.autocompleteNameValue

    this.inputTarget.value = name
    this.hiddenTarget.value = id
    this.cachedHtml = null
    this.hideResults()
    this.updateClearButtonVisibility()

    // fire native change so other controllers can bind `change->...`
    this.hiddenTarget.dispatchEvent(new Event('change', { bubbles: true }))

    // also fire a custom event with useful detail
    this.dispatch('change', { detail: { id, name }, bubbles: true })
  }
}
